import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Trash2, Pen } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const SignaturePad = ({ onSave, onCancel, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [context, setContext] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setContext(ctx);

      // Load existing signature if available
      if (existingSignature) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          setHasDrawn(true);
        };
        img.src = existingSignature;
      }
    }
  }, [existingSignature]);

  const startDrawing = (e) => {
    if (!context) return;
    setIsDrawing(true);
    setHasDrawn(true);
    const { offsetX, offsetY } = e.nativeEvent;
    context.beginPath();
    context.moveTo(offsetX, offsetY);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    const { offsetX, offsetY } = e.nativeEvent;
    context.lineTo(offsetX, offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!context) return;
    const canvas = canvasRef.current;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const saveSignature = () => {
    if (!hasDrawn) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          className="w-full bg-white rounded cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Dessinez votre signature avec la souris ou le trackpad
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={clearCanvas} className="gap-2">
            <Trash2 className="w-4 h-4" />
            Effacer
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
          <Button size="sm" onClick={saveSignature} className="gap-2" disabled={!hasDrawn}>
            <Save className="w-4 h-4" />
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SignatureDisplay = ({ signature, onEdit, onDelete, canEdit = true }) => {
  if (!signature) {
    return (
      <Alert>
        <AlertDescription>
          Aucune signature enregistrée. Cliquez sur "Créer ma signature" pour en ajouter une.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Signature enregistrée</CardTitle>
        <CardDescription>Votre signature électronique</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <img src={signature} alt="Signature" className="max-h-32 mx-auto" />
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
              <Pen className="w-4 h-4" />
              Modifier
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Supprimer
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
